<script lang="ts" setup>
  import {
    XIcon,
    FileIcon,
    InfoIcon,
    DownloadIcon,
    FileTextIcon,
    CheckCircleIcon,
    CircleIcon,
    AlertCircleIcon,
    CircleCheck,
    LucideTrash2,
    MoreHorizontalIcon,
    LoaderCircle,
    ExternalLinkIcon,
    MoreVertical,
    Trash2
  } from 'lucide-vue-next'
  import { computed, ref, onMounted, watch, onBeforeUnmount } from 'vue'
  import type { CustomerCase, Document } from '@/types/generated'
  import DocumentUpload from '@/components/app/cases/documents/DocumentUpload.vue'
  import Popover from '@/components/ui/popover/Popover.vue'
  import PopoverTrigger from '@/components/ui/popover/PopoverTrigger.vue'
  import PopoverContent from '@/components/ui/popover/PopoverContent.vue'
  import AlertDialog from '@/components/ui/alert-dialog/AlertDialog.vue'
  import AlertDialogTrigger from '@/components/ui/alert-dialog/AlertDialogTrigger.vue'
  import AlertDialogContent from '@/components/ui/alert-dialog/AlertDialogContent.vue'
  import AlertDialogHeader from '@/components/ui/alert-dialog/AlertDialogHeader.vue'
  import AlertDialogTitle from '@/components/ui/alert-dialog/AlertDialogTitle.vue'
  import AlertDialogDescription from '@/components/ui/alert-dialog/AlertDialogDescription.vue'
  import AlertDialogFooter from '@/components/ui/alert-dialog/AlertDialogFooter.vue'
  import AlertDialogCancel from '@/components/ui/alert-dialog/AlertDialogCancel.vue'
  import AlertDialogAction from '@/components/ui/alert-dialog/AlertDialogAction.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { DocumentsService } from '@/types/generated'
  import { genericErrorToast } from '@/lib/toasts'
  import { useRouter } from 'vue-router'
  import Tooltip from '@/components/ui/tooltip/Tooltip.vue'
  import TooltipContent from '@/components/ui/tooltip/TooltipContent.vue'
  import TooltipProvider from '@/components/ui/tooltip/TooltipProvider.vue'
  import TooltipTrigger from '@/components/ui/tooltip/TooltipTrigger.vue'
  import { emitter } from '@/store/app/case/detail/eventEmitter'

  const { t } = useTranslation({
    en: {
      Document: 'Document',
      Documents: 'Documents',
      KIChatHasAccessToAllDocumentsInTheRequest:
        'KI-Chat has access to all documents in the request.',
      MaximumSize: 'Maximum size',
      BrowseFiles: 'browse',
      DropYourFilesHere: 'Drop your files here or',
      DataExtraction: 'Data Extraction',
      BasicAIAnalysis: 'Basic AI Analysis',
      Summary: 'Summary',
      AdvancedAIAnalysis: 'Advanced AI Analysis',
      SummaryEmbedding: 'Summary Embedding',
      Analysis: 'Analysis',
      Queued: 'Queued',
      NoDocumentsYet: 'No documents yet',
      DeleteDocument: 'Delete document',
      DownloadDocument: 'Download document',
      confirm_delete: 'Delete Document',
      confirm_delete_document_message: 'Are you sure you want to delete this document?',
      delete: 'Delete',
      cancel: 'Cancel',
      document_deleted: 'Document was successfully deleted',
      error_deleting_document: 'Error deleting document'
    },
    de: {
      Document: 'Dokument',
      Documents: 'Dokumente',
      KIChatHasAccessToAllDocumentsInTheRequest:
        'KI-Chat hat Zugriff auf alle Dokumente in der Anfrage.',
      MaximumSize: 'Maximale Größe',
      BrowseFiles: 'durchsuchen',
      DropYourFilesHere: 'Legen Sie Ihre Datei hier ab oder',
      DataExtraction: 'Datenextraktion',
      BasicAIAnalysis: 'Grundlegende KI-Analyse',
      Summary: 'Zusammenfassung',
      AdvancedAIAnalysis: 'Fortgeschrittene KI-Analyse',
      SummaryEmbedding: 'Zusammenfassungs-Embedding',
      Analysis: 'Analyse',
      Queued: 'In Warteschlange',
      NoDocumentsYet: 'Keine Dokumente vorhanden',
      DeleteDocument: 'Dokument löschen',
      DownloadDocument: 'Dokument herunterladen',
      confirm_delete: 'Dokument löschen',
      confirm_delete_document_message: 'Sind Sie sicher, dass Sie dieses Dokument löschen möchten?',
      delete: 'Löschen',
      cancel: 'Abbrechen',
      document_deleted: 'Dokument wurde erfolgreich gelöscht',
      error_deleting_document: 'Fehler beim Löschen des Dokuments'
    }
  })

  const props = defineProps<{
    caseData: CustomerCase | null
  }>()

  const caseStore = useCaseStore()
  const router = useRouter()
  const emit = defineEmits<{
    (e: 'fileAiReady'): void
    (e: 'documentsProcessingStart', count: number): void
    (e: 'documentsProcessingComplete'): void
    (e: 'fileError', documentUuid: string, errorMessage: string): void
  }>()

  const isOpen = ref(false)
  const showInfoBanner = ref(true)
  const processingStatusText = ref<Record<string, string>>({})
  const processingTimers = ref<Record<string, number>>({})
  const openPopoverId = ref<string | null>(null)
  const showDeleteConfirmation = ref(false)
  const documentToDelete = ref<Document | null>(null)

  const isDataInitialized = ref(false)
  const documentsLoading = ref(true)

  // Keep track of all seen documents to avoid disappearing
  const seenDocuments = ref(new Map())

  // Track documents with errors separately
  const errorDocuments = ref(new Map())

  // Helper functions
  const getThumbnailUrl = (doc: Document) => {
    if (!doc.images?.length) return null
    const thumbnail = doc.images.find((img: any) => img.image_preset === 'thumbnail')
    return thumbnail?.url || null
  }

  const truncateFilename = (filename: string, maxLength: number = 18) => {
    if (!filename || filename.length <= maxLength) return filename

    const extension = filename.split('.').pop()
    const name = filename.substring(0, filename.lastIndexOf('.'))

    const truncatedLength = maxLength - extension!.length - 3 // -3 for "..." and "."
    const truncatedName = name.substring(0, truncatedLength) + '...'

    return `${truncatedName}.${extension}`
  }

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
  }

  const downloadDocument = (doc: Document) => {
    if (!doc.url) return

    const link = document.createElement('a')
    link.href = doc.url
    link.download = doc.filename || 'document'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const openDocumentInNewTab = (doc: Document) => {
    if (!doc.url) return

    window.open(doc.url, '_blank')
  }

  const togglePopover = (id: string) => {
    openPopoverId.value = id === openPopoverId.value ? null : id
  }

  const confirmDeleteDocument = (document: Document) => {
    documentToDelete.value = document
    showDeleteConfirmation.value = true
    // Close all popovers when showing the confirm dialog
    openPopoverId.value = null
    isOpen.value = false
  }

  const deleteDocument = () => {
    if (documentToDelete.value?.uuid) {
      // Call the API to delete the document
      DocumentsService.documentsDestroy({ uuid: documentToDelete.value.uuid })
        .then(() => {
          console.log('Document deleted successfully')
          // After successful deletion, refresh the case data
          if (props.caseData?.uuid) {
            caseStore.fetchCaseWithErrorHandling(props.caseData.uuid)
          }
        })
        .catch((error) => {
          // Show error toast
          console.error('Error deleting document:', error)
          genericErrorToast()
        })
        .finally(() => {
          // Close the dialog and reset the document to delete
          showDeleteConfirmation.value = false
          documentToDelete.value = null
        })
    } else {
      showDeleteConfirmation.value = false
      documentToDelete.value = null
    }
  }

  const cancelUpload = (uploadId: string) => {
    if (uploadId) {
      caseStore.removeUpload(uploadId)
    }
  }

  // Start a timer to update the processing time display
  function startProcessingTimer(documentUuid: string) {
    // Initialize with current elapsed time
    const processState = caseStore.getDocumentProcessingState(documentUuid)
    if (processState?.processStartedAt) {
      updateProcessingTime(documentUuid, processState.processStartedAt)
    }

    // Set up interval to update time
    processingTimers.value[documentUuid] = window.setInterval(() => {
      const state = caseStore.getDocumentProcessingState(documentUuid)
      if (state?.processStartedAt) {
        updateProcessingTime(documentUuid, state.processStartedAt)
      }

      // If document is no longer processing, stop the timer
      if (!caseStore.isDocumentInProcess(documentUuid)) {
        clearInterval(processingTimers.value[documentUuid])
        delete processingTimers.value[documentUuid]
      }
    }, 1000)
  }

  // Update the processing time display
  function updateProcessingTime(documentUuid: string, startTime: string) {
    const start = new Date(startTime).getTime()
    const now = new Date().getTime()
    const elapsed = Math.floor((now - start) / 1000) // seconds

    if (elapsed < 60) {
      processingStatusText.value[documentUuid] = `${elapsed}s`
    } else {
      const minutes = Math.floor(elapsed / 60)
      const seconds = elapsed % 60
      processingStatusText.value[documentUuid] = `${minutes}m ${seconds}s`
    }
  }

  // Computed properties for document lists

  // Update the allDocuments computed property in DocumentsPopover.vue
  const allDocuments = computed(() => {
    if (!isDataInitialized.value || documentsLoading.value) {
      return []
    }

    const documents: {
      uuid?: string
      id?: string
      type: 'upload' | 'processing' | 'complete' | 'error'
      document?: Document
      uploadInfo?: any
      progress?: number
      filename?: string
      errorType?: 'ocr' | 'embeddings' | 'other'
      errorMessage?: string
    }[] = []

    // Create a Map to prevent duplicate entries
    const documentMap = new Map()

    // Track document UUIDs that are in processingDocuments to prevent duplicates
    const processingDocumentUuids = new Set()

    // Helper to get document priority (to decide which state to show if multiple exist)
    const getPriority = (type: 'upload' | 'processing' | 'complete' | 'error') => {
      return { error: 4, complete: 3, processing: 2, upload: 1 }[type] || 0
    }

    // First, add any documents with errors (these take highest priority)
    errorDocuments.value.forEach((docItem, uuid) => {
      documentMap.set(uuid, docItem)
      processingDocumentUuids.add(uuid)
    })

    // Process processing documents NEXT
    caseStore.processingDocuments.forEach((doc) => {
      if (!doc.uuid) return

      // Skip if already added as an error document
      if (processingDocumentUuids.has(doc.uuid)) return

      // Track this UUID as being in processing state
      processingDocumentUuids.add(doc.uuid)

      // Debug logging
      console.log('Processing document:', doc.uuid, doc.filename || doc.title)
      console.log('Document data:', doc)

      // Direct check for errors in the document object
      const hasOcrError = doc.ocr_error && doc.ocr_error.length > 0
      const hasEmbeddingsError = doc.embeddings_error && doc.embeddings_error.length > 0
      const hasMetadataError =
        doc.metadata_enrichment_error && doc.metadata_enrichment_error.length > 0

      console.log(
        'Direct check - OCR error:',
        hasOcrError,
        'Embeddings error:',
        hasEmbeddingsError,
        'Metadata error:',
        hasMetadataError
      )

      // Check for critical errors first - using direct check
      if (hasMetadataError) {
        console.log('Document has critical error, setting error state')
        const key = doc.uuid
        // Use a generic user-friendly error message instead of the actual error
        const errorMessage = 'AI analysis failed\nPlease upload another file'

        const docItem = {
          uuid: doc.uuid,
          type: 'error' as const,
          document: doc,
          progress: 100,
          filename: doc.filename || doc.title,
          errorType: 'analysis',
          errorMessage
        }

        documentMap.set(key, docItem)

        // Also add to error documents
        errorDocuments.value.set(key, docItem)
      } else {
        console.log('Document has no critical error, setting processing state')
        const key = doc.uuid
        const docItem = {
          uuid: doc.uuid,
          type: 'processing' as const,
          document: doc,
          progress: 100,
          filename: doc.filename || doc.title
        }

        documentMap.set(key, docItem)
      }

      // Start processing timer if not already started
      if (!processingTimers.value[doc.uuid]) {
        startProcessingTimer(doc.uuid)
      }
    })

    // Process all active uploads - only add if the document isn't already in processing state
    // AND doesn't have a documentUuid that matches a completed document
    caseStore.activeUploadsArray.forEach((upload) => {
      if (upload.caseUuid === props.caseData?.uuid) {
        // If this upload has a documentUuid AND that UUID is already in the processing state,
        // skip adding it to avoid duplication
        if (upload.documentUuid && processingDocumentUuids.has(upload.documentUuid)) {
          return
        }

        // Also skip if this upload's documentUuid matches a completed document
        if (
          upload.documentUuid &&
          caseStore.caseRecord?.documents?.some((d) => d.uuid === upload.documentUuid)
        ) {
          return
        }

        // Use upload.id as the key for uploads to ensure uniqueness
        const key = upload.id
        const docItem = {
          uuid: upload.documentUuid,
          id: upload.id,
          type: 'upload' as const,
          uploadInfo: upload,
          progress: upload.progress,
          filename: upload.filename
        }

        documentMap.set(key, docItem)
      }
    })

    // Process all completed documents
    if (caseStore.caseRecord?.documents) {
      caseStore.caseRecord.documents.forEach((doc) => {
        if (!doc.uuid) return

        // Only consider as complete if not in processing state
        if (!caseStore.isDocumentInProcess(doc.uuid) && !processingDocumentUuids.has(doc.uuid)) {
          const key = doc.uuid
          const docItem = {
            uuid: doc.uuid,
            type: 'complete' as const,
            document: doc,
            filename: doc.filename || doc.title
          }

          // Replace any existing item with same key if this has higher priority
          if (
            !documentMap.has(key) ||
            getPriority('complete') > getPriority(documentMap.get(key).type)
          ) {
            documentMap.set(key, docItem)
          }
        }
      })
    }

    // Convert map values to array for display
    const documentsArray = Array.from(documentMap.values())

    // Sort documents by uploaded date (newest first)
    return documentsArray.sort((a, b) => {
      // Uploads and processing documents should be at the top
      if (a.type === 'upload' && b.type !== 'upload') return -1
      if (a.type !== 'upload' && b.type === 'upload') return 1

      // Get timestamps for comparison
      const getTimestamp = (item: any) => {
        // For uploads, use current time to ensure they're at the top
        if (item.type === 'upload') return Date.now()

        // For documents, use uploaded_at or created_at timestamp
        if (item.document) {
          // Try uploaded_at first, then created_at as fallback
          const timestamp = item.document.uploaded_at || item.document.created_at
          return timestamp ? new Date(timestamp).getTime() : 0
        }

        return 0
      }

      // Sort by timestamp (descending - newest first)
      const timestampA = getTimestamp(a)
      const timestampB = getTimestamp(b)

      return timestampB - timestampA
    })
  })

  const onFileError = (documentUuid: string, errorMessage?: string) => {
    console.log('File processing error:', documentUuid)

    // Find the document in the case record
    const doc = caseStore.caseRecord?.documents?.find((d) => d.uuid === documentUuid)
    if (!doc) {
      console.error('Could not find document with UUID:', documentUuid)
      return
    }

    // Use the provided error message or fallback to default
    const displayErrorMessage = errorMessage || 'AI analysis failed\nPlease upload another file'

    // Create an error document item
    const docItem = {
      uuid: documentUuid,
      type: 'error' as const,
      document: doc,
      progress: 100,
      filename: doc.filename || doc.title,
      errorType: 'analysis',
      errorMessage: displayErrorMessage
    }

    // Add to error documents map
    errorDocuments.value.set(documentUuid, docItem)

    // Force a UI update by refreshing the case data
    caseStore.fetchCaseWithErrorHandling(props.caseData?.uuid).then(() => {
      // Force a recomputation of allDocuments
      // This ensures the UI updates immediately without waiting for other updates
      const temp = [...allDocuments.value]
      allDocuments.value = [] // This is just to trigger reactivity
      setTimeout(() => {
        // We use setTimeout to ensure the reset happens in a separate tick
        allDocuments.value = temp
      }, 0)
    })

    // Emit the error event to parent components
    emit('fileError', documentUuid, errorMessage)
  }

  // Poll for document updates periodically
  const pollInterval = ref<number | null>(null)

  async function startPolling() {
    if (pollInterval.value) return

    // First, do an immediate check
    checkDocumentsForErrors()

    pollInterval.value = window.setInterval(() => {
      checkDocumentsForErrors()
    }, 3000) // Poll every 3 seconds (reduced from 5)
  }

  // New function to check documents for errors
  async function checkDocumentsForErrors() {
    if (props.caseData?.uuid) {
      // Check if there are any documents in processing state
      const hasProcessingDocuments = caseStore.processingDocuments.length > 0

      // If no documents are in processing state, stop polling
      // But make sure we don't have any documents that were just added and might not be in processing state yet
      if (!hasProcessingDocuments && !caseStore.hasActiveUploads) {
        console.log('No documents in processing state and no active uploads, stopping polling')
        stopPolling()
        return
      }

      // Refresh case data
      await caseStore.fetchCaseWithErrorHandling(props.caseData.uuid)

      // Use the store's computed property for critical errors
      caseStore.documentsWithCriticalErrors.forEach((doc) => {
        if (doc.uuid) {
          // Only add if not already in our error map
          if (!errorDocuments.value.has(doc.uuid)) {
            console.log('Document has critical error from polling:', doc.uuid)
            console.log('Metadata error:', doc.metadata_enrichment_error)

            // Use a generic user-friendly error message
            const errorMessage = 'AI analysis failed\nPlease upload another file'

            const docItem = {
              uuid: doc.uuid,
              type: 'error' as const,
              document: doc,
              progress: 100,
              filename: doc.filename || doc.title,
              errorType: 'analysis',
              errorMessage
            }

            errorDocuments.value.set(doc.uuid, docItem)
          }
        }
      })
    }
  }

  function stopPolling() {
    if (pollInterval.value) {
      clearInterval(pollInterval.value)
      pollInterval.value = null
    }
  }

  // Start polling when component is mounted
  onMounted(() => {
    // Add event listener for document errors
    emitter.on('document-error', (event) => {
      console.log('Received document error event:', event)
      onFileError(event.documentUuid, event.errorMessage)
    })

    startPolling()
  })

  // Clean up when component is unmounted
  onBeforeUnmount(() => {
    // Remove event listener for document errors
    emitter.off('document-error')

    stopPolling()
  })

  // Add this to the component's setup to track document availability
  const pollForNewDocuments = () => {
    if (!props.caseData?.uuid) return

    // Create a polling mechanism to ensure documents appear
    // This is a safety measure to handle cases where normal reactivity fails
    let pollCount = 0
    const maxPolls = 20 // Max 20 polls (20 seconds)

    const pollInterval = setInterval(() => {
      pollCount++

      // Fetch latest case data to ensure documents are up to date
      caseStore.fetchCaseWithErrorHandling(props.caseData.uuid)

      // Stop after max polls or if no uploads are in progress
      if (pollCount >= maxPolls || !caseStore.hasActiveUploads) {
        clearInterval(pollInterval)
      }
    }, 1000) // Poll every second

    // Clear interval on component unmount
    onBeforeUnmount(() => {
      clearInterval(pollInterval)
    })
  }

  // Modify the onFileUploaded function to start document polling
  const onFileUploaded = (documentUuid: string) => {
    console.log('File uploaded:', documentUuid)
    pollForNewDocuments()
  }

  const onFileAiReady = (documentUuid: string) => {
    emit('fileAiReady')
  }

  const onFileAiComplete = (documentUuid: string) => {
    console.log('File AI processing complete:', documentUuid)
  }

  const onFilesSelected = (files: File[]) => {
    console.log('Files selected:', files.length)
  }

  // Watch for popover open/close
  watch(
    () => isOpen.value,
    async (newValue) => {
      if (newValue) {
        // When opening the popover, use existing case data if available
        if (props.caseData?.uuid) {
          documentsLoading.value = true
          try {
            // Only fetch if we don't have case data or documents
            const shouldFetch =
              !caseStore.caseRecord ||
              !caseStore.caseRecord.documents ||
              caseStore.caseRecord.documents.length === 0

            if (shouldFetch) {
              // Only fetch if necessary
              console.log('Fetching case data because we have no documents')
              await caseStore.fetchCaseWithErrorHandling(props.caseData.uuid)
            } else {
              console.log(
                'Using existing case data, documents count:',
                caseStore.caseRecord.documents.length
              )
            }

            // Use the store's computed property for critical errors instead of checking each document
            caseStore.documentsWithCriticalErrors.forEach((doc) => {
              if (doc.uuid) {
                console.log('Document has critical error on popover open:', doc.uuid)
                console.log('Metadata error:', doc.metadata_enrichment_error)

                // Add to error documents map
                const docItem = {
                  uuid: doc.uuid,
                  type: 'error' as const,
                  document: doc,
                  progress: 100,
                  filename: doc.filename || doc.title,
                  errorType: 'analysis',
                  errorMessage: 'AI analysis failed\nPlease upload another file'
                }

                errorDocuments.value.set(doc.uuid, docItem)
              }
            })

            // Start polling for updates only if there are processing documents
            if (caseStore.processingDocuments.length > 0 || caseStore.hasActiveUploads) {
              startPolling()
            }
          } catch (error) {
            console.error('Error fetching case data:', error)
          } finally {
            documentsLoading.value = false
          }
        }
      } else {
        // When closing the popover, stop polling
        stopPolling()
      }
    }
  )

  // Initialize timers for processing documents
  onMounted(async () => {
    if (props.caseData?.uuid) {
      documentsLoading.value = true

      try {
        // Fetch case data
        await caseStore.fetchCaseWithErrorHandling(props.caseData.uuid)

        // Wait a brief moment for processing status to be established
        await new Promise((resolve) => setTimeout(resolve, 100))

        // Initialize processing timers
        if (caseStore.processingDocuments.length > 0) {
          caseStore.processingDocuments.forEach((doc) => {
            if (doc.uuid && !processingTimers.value[doc.uuid]) {
              startProcessingTimer(doc.uuid)
            }
          })
        }

        // Now we can consider data initialized
        isDataInitialized.value = true
      } finally {
        documentsLoading.value = false
      }
    }
  })

  // Clean up timers when component unmounts
  onBeforeUnmount(() => {
    Object.keys(processingTimers.value).forEach((uuid) => {
      clearInterval(processingTimers.value[uuid])
      delete processingTimers.value[uuid]
    })
  })
</script>

<template>
  <Popover v-model:open="isOpen">
    <PopoverTrigger
      class="flex items-center gap-1.5 text-xs hover:text-gray-700 dark:hover:text-neutral-300"
    >
      <FileTextIcon class="h-3.5 w-3.5" />
      <span>
        {{
          allDocuments.length === 1
            ? `1 ${t('Document')}`
            : `${allDocuments.length} ${t('Documents')}`
        }}
      </span>
    </PopoverTrigger>

    <PopoverContent
      class="w-[320px] rounded-2xl p-0 shadow-lg"
      side="bottom"
      align="end"
      :side-offset="8"
    >
      <!-- Header -->
      <div class="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <h3 class="text-lg font-medium">{{ t('Documents') }} ({{ allDocuments.length }})</h3>
        <button
          class="text-gray-500 hover:text-gray-700"
          @click="isOpen = false"
        >
          <XIcon class="h-5 w-5" />
        </button>
      </div>

      <!-- Info Banner -->
      <!-- <div
        v-if="showInfoBanner"
        class="mx-4 mt-3 flex items-center gap-3 rounded-md bg-blue-50 p-3 text-sm text-blue-700"
      >
        <InfoIcon class="h-4 w-4 shrink-0 text-blue-600" />
        <span>{{ t('KIChatHasAccessToAllDocumentsInTheRequest') }}</span>
        <button
          class="ml-auto"
          @click="showInfoBanner = false"
        >
          <XIcon class="h-4 w-4" />
        </button>
      </div> -->

      <div class="flex max-h-[500px] flex-col">
        <div class="flex-1 overflow-y-auto p-4">
          <div
            v-if="documentsLoading"
            class="flex items-center justify-center p-8"
          >
            <LoaderCircle class="h-8 w-8 animate-spin text-violet-500" />
          </div>
          <!-- No documents yet -->
          <div
            v-else-if="allDocuments.length === 0"
            class="flex h-full items-center justify-center text-sm text-gray-500"
          >
            <div class="text-center">
              <FileTextIcon class="mx-auto h-12 w-12 text-gray-300" />
              <p class="mt-2">{{ t('NoDocumentsYet') }}</p>
            </div>
          </div>

          <!-- Document items -->
          <div
            v-else
            class="space-y-2"
          >
            <div
              v-for="item in allDocuments"
              :key="item.uuid || item.id || Math.random()"
              class="flex items-center rounded-md p-1.5 transition-colors hover:bg-gray-50"
            >
              <!-- Document thumbnail - Left side -->
              <div class="mr-3 flex-shrink-0">
                <div class="h-12 w-12 overflow-hidden rounded border border-gray-100 bg-white">
                  <img
                    v-if="item.document && getThumbnailUrl(item.document)"
                    :src="getThumbnailUrl(item.document)"
                    alt="Document thumbnail"
                    class="h-full w-full object-cover"
                  />
                  <FileIcon
                    v-else
                    class="h-full w-full p-2 text-gray-400"
                  />
                </div>
              </div>

              <!-- Document info - Middle section -->
              <div class="min-w-0 flex-1">
                <!-- Document name -->
                <div
                  class="truncate text-sm font-medium"
                  :class="[
                    item.type === 'upload'
                      ? 'text-gray-800'
                      : 'group cursor-pointer hover:text-violet-600'
                  ]"
                  @click="
                    item.type !== 'upload' &&
                    item.document?.url &&
                    openDocumentInNewTab(item.document)
                  "
                >
                  <span class="inline-flex items-center gap-1 group-hover:underline">
                    {{ truncateFilename(item.filename || 'Dokument123.pdf') }}
                    <ExternalLinkIcon
                      v-if="item.type !== 'upload'"
                      class="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100"
                    />
                  </span>
                </div>

                <!-- Document date or progress -->
                <div
                  v-if="item.type === 'upload'"
                  class="mt-1"
                >
                  <div class="flex items-center gap-2">
                    <div class="h-1.5 w-full rounded-full bg-gray-200">
                      <div
                        class="h-1.5 rounded-full bg-violet-500"
                        :style="{ width: `${item.progress || 30}%` }"
                      ></div>
                    </div>
                    <span class="whitespace-nowrap text-sm font-medium text-gray-700">
                      {{ item.progress || 30 }}%
                    </span>
                  </div>
                </div>
                <div
                  v-else-if="item.type === 'processing'"
                  class="mt-1"
                ></div>
                <div
                  v-else-if="item.type === 'error'"
                  class="mt-1 text-sm text-gray-500"
                >
                  {{ item.document?.created_at ? formatDate(item.document.created_at) : '' }}
                </div>
                <div
                  v-else
                  class="mt-1 text-sm text-gray-500"
                >
                  {{ item.document?.created_at ? formatDate(item.document.created_at) : '' }}
                </div>
              </div>

              <!-- Status indicators -->
              <div
                v-if="item.type === 'processing'"
                class="mr-2 flex-shrink-0"
              >
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger class="flex-shrink-0">
                      <div class="h-5 w-5 animate-spin">
                        <LoaderCircle class="h-5 w-5 text-violet-500" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent
                      class="max-w-[200px] rounded-md border-0 bg-gray-800 px-3 py-2 text-white shadow-lg"
                      :side="'top'"
                    >
                      <div class="flex flex-col">
                        <p class="text-sm font-medium">AI analysis in progress</p>
                        <p class="text-xs text-gray-300">The AI can produce incorrect results</p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <TooltipProvider v-else-if="item.type === 'error'">
                <Tooltip>
                  <TooltipTrigger class="mr-2 flex-shrink-0">
                    <AlertCircleIcon class="h-5 w-5 text-red-500" />
                  </TooltipTrigger>
                  <TooltipContent
                    class="max-w-[200px] rounded-md border-0 bg-gray-800 px-3 py-2 text-white shadow-lg"
                    :side="'top'"
                  >
                    <div class="flex flex-col">
                      <p class="text-sm font-medium">AI analysis failed</p>
                      <p class="text-xs text-gray-300">Please upload another file</p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <div
                v-else-if="item.type === 'complete'"
                class="mr-2 flex-shrink-0"
              >
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger class="flex-shrink-0">
                      <CircleCheck class="h-5 w-5 text-green-500" />
                    </TooltipTrigger>
                    <TooltipContent
                      class="max-w-[200px] rounded-md border-0 bg-gray-800 px-3 py-2 text-white shadow-lg"
                      :side="'top'"
                    >
                      <div class="flex flex-col">
                        <p class="text-sm font-medium">Ready for AI</p>
                        <p class="text-xs text-gray-300">
                          You can ask questions about this document
                        </p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              <!-- Action menu -->
              <div class="flex-shrink-0">
                <!-- Trash button for uploads/processing (cancels upload) -->
                <button
                  v-if="item.type === 'upload' || item.type === 'processing'"
                  class="text-black-400 hover:text-black-700 flex h-8 w-8 items-center justify-center rounded-md border
                    border-gray-100 bg-white shadow-sm"
                  :disabled="item.type === 'processing'"
                  :class="{ 'cursor-not-allowed opacity-50': item.type === 'processing' }"
                  @click.stop="cancelUpload(item.uploadInfo?.id)"
                >
                  <LucideTrash2 class="text-black-500 h-4 w-4" />
                </button>

                <!-- More options popover for completed documents -->
                <Popover
                  v-else
                  :open="openPopoverId === item.uuid"
                  @update:open="
                    (val) => (val ? togglePopover(item.uuid || '') : (openPopoverId = null))
                  "
                >
                  <PopoverTrigger
                    class="text-black-400 hover:text-black-700 flex h-8 w-8 items-center justify-center rounded-md border
                      border-gray-100 bg-white shadow-sm"
                  >
                    <MoreVertical class="h-4 w-4" />
                  </PopoverTrigger>
                  <PopoverContent class="w-48 p-1">
                    <div class="flex flex-col">
                      <!-- Download button -->
                      <button
                        class="flex w-full items-center rounded-md px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
                        @click.stop="downloadDocument(item.document!)"
                      >
                        <DownloadIcon class="mr-2 h-4 w-4" />
                        {{ t('DownloadDocument') }}
                      </button>

                      <!-- Delete button -->
                      <button
                        class="flex w-full items-center rounded-md px-2 py-1.5 text-sm text-red-600 hover:bg-red-50"
                        @click.stop="confirmDeleteDocument(item.document!)"
                      >
                        <Trash2 class="mr-2 h-4 w-4" />
                        {{ t('DeleteDocument') }}
                      </button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
        </div>

        <!-- Document Upload Area -->
        <div class="mt-auto border-t border-gray-200 p-4">
          <DocumentUpload
            v-if="props.caseData"
            ref="documentUpload"
            :case-data="props.caseData"
            :max-size="100"
            :auto-remove-after-ai-ready="false"
            :auto-remove-after-ai-complete="true"
            @file-uploaded="onFileUploaded"
            @file-ai-ready="onFileAiReady"
            @file-ai-complete="onFileAiComplete"
            @file-error="onFileError"
            @files-selected="onFilesSelected"
          />
        </div>
      </div>
    </PopoverContent>
  </Popover>

  <!-- Delete Confirmation Modal -->
  <AlertDialog
    :open="showDeleteConfirmation"
    class="z-50"
    @update:open="showDeleteConfirmation = $event"
  >
    <AlertDialogContent class="z-50">
      <AlertDialogHeader>
        <AlertDialogTitle>{{ t('confirm_delete') }}</AlertDialogTitle>
        <AlertDialogDescription>
          {{ t('confirm_delete_document_message') }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel @click="showDeleteConfirmation = false">
          {{ t('cancel') }}
        </AlertDialogCancel>
        <AlertDialogAction
          class="bg-red-600 hover:bg-red-700"
          @click="deleteDocument()"
        >
          {{ t('delete') }}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>

<style scoped>
  .pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }

  .pulse-opacity {
    animation: pulse-opacity 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }

  @keyframes pulse-opacity {
    0%,
    100% {
      opacity: 0.9;
    }
    50% {
      opacity: 0.6;
    }
  }
</style>
